import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../theme.js";
import Logo from "./Logo.jsx";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <div className="container-x pt-4">
        <nav
          className={`flex items-center justify-between gap-4 rounded-full px-4 py-2.5 transition-all duration-500 ${
            scrolled
              ? "border border-white/40 bg-white/70 backdrop-blur-xl shadow-glass"
              : "border border-white/40 bg-white/70 backdrop-blur-xl shadow-glass"
          }`}
        >
          <Logo />

          <ul className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="group relative rounded-full px-4 py-2 text-sm font-medium text-navy-800 transition hover:text-navy-900"
                >
                  <span className="relative z-10">{link.label}</span>
                  <span className="absolute bottom-1 left-1/2 h-0.5 w-0 -translate-x-1/2 bg-sand-500 transition-all duration-300 group-hover:w-3/4" />
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href="#kontakt"
              className="hidden md:inline-flex btn-primary !px-5 !py-2.5"
            >
              Starta projekt
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-full border border-navy-900/10 bg-white/70 text-navy-900 backdrop-blur md:hidden"
              aria-label={open ? "Stäng meny" : "Öppna meny"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="container-x mt-3 md:hidden"
          >
            <div className="rounded-3xl border border-white/40 bg-white/85 p-6 shadow-glass backdrop-blur-xl">
              <ul className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <li key={link.href}>
                    <a
                      onClick={() => setOpen(false)}
                      href={link.href}
                      className="flex items-center justify-between rounded-2xl px-4 py-3 text-base font-medium text-navy-900 hover:bg-sand-100"
                    >
                      {link.label}
                      <ArrowUpRight className="h-4 w-4 text-navy-500" />
                    </a>
                  </li>
                ))}
              </ul>
              <a
                href="#kontakt"
                onClick={() => setOpen(false)}
                className="btn-primary mt-4 w-full"
              >
                Starta projekt
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
